import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { startGeneration } from './generationApi';
import { clearGenerationIntent, createGenerationIntent, readGenerationIntent } from './generationIntent';
import { generationKeys } from './useGenerationQueries';

function restoreIntent(): { key: string | null; failed: boolean } {
  try { return { key: readGenerationIntent(), failed: false }; } catch { return { key: null, failed: true }; }
}
export function useStartGeneration(select: (id: string) => void) {
  const [intent, setIntent] = useState(restoreIntent);
  const [storageFailed, setStorageFailed] = useState(false);
  const pending = useRef(false);
  const client = useQueryClient();
  const mutation = useMutation({ mutationFn: startGeneration, retry: false });
  const confirm = async (): Promise<boolean> => {
    if (pending.current || intent.failed) return false;
    pending.current = true;
    try {
      let key = intent.key;
      if (key === null) {
        try { key = createGenerationIntent(); setIntent({ key, failed: false }); } catch { setStorageFailed(true); return false; }
      }
      const job = await mutation.mutateAsync(key);
      try { clearGenerationIntent(); } catch { /* The known UUID resolves this intent in memory; replay after reload remains safe. */ }
      setIntent({ key: null, failed: false });
      client.setQueryData(generationKeys.detail(job.id), job);
      select(job.id);
      void client.invalidateQueries({ queryKey: generationKeys.all });
      return true;
    } catch {
      void client.invalidateQueries({ queryKey: generationKeys.all });
      return false;
    } finally { pending.current = false; }
  };
  return { confirm, mutation, hasIntent: intent.key !== null, storageFailed: storageFailed || intent.failed };
}
